import { Delete, Edit, AdminPanelSettings, MoreVert } from '@mui/icons-material';
import {
    Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Menu, MenuItem, ListItemIcon,
    TextField
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import {
    ServiceAuthToken,
    useDeleteServiceAuthToken,
    useGetAllServiceAuthTokens,
    useRenameServiceAuthToken
} from '../api/authTokens';
import LoadingState from './Common/LoadingState';
import { formatDateTime } from '../utils/misc';
import clsx from 'clsx';

const AdminTokens: React.FC = () => {
    const [editToken, setEditToken] = useState<ServiceAuthToken | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpenedForId, setMenuOpenedForId] = useState<string>('');
    const queryClient = useQueryClient();

    // Fetch tokens using existing hook
    const { data: tokens, isLoading, refetch: refetchAuthTokens } = useGetAllServiceAuthTokens();

    // Delete token mutation using existing function
    const { mutate: deleteToken } = useDeleteServiceAuthToken();

    // Update token name mutation using existing function
    const { mutate: updateToken } = useRenameServiceAuthToken();

    const handleEdit = (token: ServiceAuthToken) => {
        setEditToken(token);
        setIsEditDialogOpen(true);
        setMenuAnchorEl(null);
        setMenuOpenedForId('');
    };

    const handleDelete = (token: ServiceAuthToken) => {
        deleteToken(token.id, {
            onSuccess: () => {
                refetchAuthTokens();
            }
        });
        setMenuAnchorEl(null);
        setMenuOpenedForId('');
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, tokenId: string) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuOpenedForId(tokenId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuOpenedForId('');
    };

    return (
        <div className="bg-backgroundGrey w-full h-full flex flex-col overflow-hidden">
            {/* Header Section */}
            <div className="bg-white border-b border-borderGrey px-8 py-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-backgroundBlue rounded-lg">
                        <AdminPanelSettings className="text-nanonetsBlue" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-greyishBlueMid">OAuth Tokens</h1>
                        <p className="text-sm text-greyishBlue mt-1">
                            Manage your OAuth service authentication tokens
                        </p>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="flex-1 overflow-auto px-8 py-6">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <LoadingState />
                    </div>
                ) : (
                    <>
                        {tokens && tokens.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {tokens.map((token) => (
                                    <div
                                        key={token.id}
                                        className="bg-white rounded-lg border border-borderGrey p-5
                                                   hover:border-nanonetsBlue/30 hover:shadow-md
                                                   transition-all duration-200 flex flex-col"
                                    >
                                        {/* Card Header */}
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="font-semibold text-sm text-greyishBlueMid truncate">
                                                        {token.token_name || 'Unnamed Token'}
                                                    </h3>
                                                    <Chip
                                                        label={isTokenExpired(token) ? 'Expired' : 'Active'}
                                                        size="small"
                                                        className={clsx(
                                                            "text-xs font-medium",
                                                            isTokenExpired(token)
                                                                ? "bg-[#f9e5e5] text-[#D02B2B] border border-[#f7ddde]"
                                                                : "bg-[#ecf6ee] text-[#18855E] border border-[#84ff97]"
                                                        )}
                                                    />
                                                </div>
                                                <div className="text-xs text-greyishBlue font-mono truncate">
                                                    ID: {token.id}
                                                </div>
                                            </div>
                                            <div
                                                className="ml-2 p-1.5 rounded-md hover:bg-backgroundGrey cursor-pointer transition-colors flex-shrink-0"
                                                onClick={(e) => handleMenuOpen(e, token.id)}
                                            >
                                                <MoreVert className="text-lightSlateGrey text-lg" />
                                            </div>
                                        </div>

                                        {/* Card Content */}
                                        <div className="space-y-3 flex-1">
                                            <div>
                                                <div className="text-xs text-greyishBlue mb-1 font-medium">Service</div>
                                                <div className="text-sm text-greyishBlueMid">{token.service_id}</div>
                                            </div>
                                            {token.service_readable_info && (
                                                <div>
                                                    <div className="text-xs text-greyishBlue mb-1 font-medium">User Info</div>
                                                    <div className="text-sm text-greyishBlueMid">{token.service_readable_info}</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Card Footer */}
                                        <div className="mt-4 pt-4 border-t border-borderGrey space-y-2">
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-greyishBlue">Created</span>
                                                <span className="text-greyishBlueMid">{formatDateTime(token.created_time)}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-greyishBlue">Expires</span>
                                                <span className="text-greyishBlueMid">
                                                    {token.expiry ? formatDateTime(token.expiry) : 'No expiry'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                                <div className="p-6 bg-backgroundBlue rounded-full mb-4">
                                    <AdminPanelSettings className="text-nanonetsBlue text-4xl" />
                                </div>
                                <h3 className="text-lg font-semibold text-greyishBlueMid mb-2">No tokens found</h3>
                                <p className="text-sm text-greyishBlue text-center max-w-md">
                                    You don't have any OAuth tokens yet. Tokens will appear here once created.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                disableAutoFocusItem
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    className: "!shadow-lg !border !border-borderGrey !rounded-lg !mt-1"
                }}
            >
                <MenuItem
                    onClick={() => {
                        const token = tokens?.find(t => t.id === menuOpenedForId);
                        if (token) handleEdit(token);
                    }}
                    className="!text-xs !py-2 !px-3 hover:!bg-backgroundBlue"
                >
                    <ListItemIcon className="mr-2 !min-w-0">
                        <Edit className="!text-sm !text-greyishBlue" />
                    </ListItemIcon>
                    Edit name
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const token = tokens?.find(t => t.id === menuOpenedForId);
                        if (token && window.confirm('Are you sure you want to delete this token?')) {
                            handleDelete(token);
                        }
                    }}
                    className="!text-xs !py-2 !px-3 !text-[#C05353] hover:!bg-red-50"
                >
                    <ListItemIcon className="mr-2 !min-w-0">
                        <Delete className="!text-sm" htmlColor="#C05353" />
                    </ListItemIcon>
                    Delete token
                </MenuItem>
            </Menu>

            {/* Edit Dialog */}
            <Dialog 
                open={isEditDialogOpen} 
                onClose={() => setIsEditDialogOpen(false)}
                PaperProps={{
                    className: "!rounded-lg !shadow-xl"
                }}
            >
                <DialogTitle className="text-greyishBlueMid font-semibold">Edit Token Name</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Token Name"
                        value={editToken?.token_name || ''}
                        onChange={(e) => setEditToken(prev => 
                            prev ? {...prev, token_name: e.target.value} : null
                        )}
                        className="mt-4"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                    borderColor: 'nanonetsBlue',
                                },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions className="px-6 py-4 gap-2">
                    <Button 
                        onClick={() => setIsEditDialogOpen(false)}
                        className="!text-greyishBlue !normal-case"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => {
                            if (editToken) {
                                updateToken({
                                    tokenId: editToken.id,
                                    newName: editToken.token_name
                                });
                                setIsEditDialogOpen(false);
                            }
                        }}
                        className="!bg-nanonetsBlue !text-white !normal-case hover:!bg-nanonetsBlue/90"
                        variant="contained"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

// Helper function to check if token is expired
const isTokenExpired = (token: ServiceAuthToken): boolean => {
    if (!token.expiry) return false;
    return new Date(token.expiry) < new Date();
};

export default AdminTokens;